import React, { useState } from 'react';
import { X, AlertCircle, Send, CheckCircle } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';

interface ReportErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  questionId: string;
  questionTitle: string;
  paperId?: string;
  currentUser?: any;
}

const ERROR_TYPES = [
  { value: 'answer_error', label: '答案错误' },
  { value: 'question_error', label: '题目有误' },
  { value: 'option_error', label: '选项有误' },
  { value: 'explanation_error', label: '解析有误' },
  { value: 'other', label: '其他问题' },
];

export default function ReportErrorModal({
  isOpen,
  onClose,
  questionId,
  questionTitle,
  paperId,
  currentUser,
}: ReportErrorModalProps) {
  const [errorType, setErrorType] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleSubmit = async () => {
    if (!errorType) {
      alert('请选择纠错类型');
      return;
    }
    if (!description.trim()) {
      alert('请输入纠错说明');
      return;
    }

    setIsSubmitting(true);
    try {
      // 直接写入 error_reports 表（绕过有问题的 Edge Function）
      const { data: report, error: insertError } = await supabase
        .from('error_reports')
        .insert({
          question_id: questionId,
          question_title: questionTitle || '',
          paper_id: paperId || null,
          user_id: currentUser?.id || null,
          user_name: currentUser?.username || currentUser?.phone || '匿名用户',
          error_type: errorType,
          description: description.trim(),
          status: 'pending',
        })
        .select()
        .single();

      if (insertError) {
        throw new Error(insertError.message || '保存纠错记录失败');
      }

      // 通过 message-service 发送站内信通知给后台管理员（仅 admin，不发送给 teacher）
      try {
        const { data: recipients } = await supabase
          .from('profiles')
          .select('id')
          .eq('role', 'admin');

        if (recipients && recipients.length > 0) {
          const errorTypeMap: Record<string, string> = {
            answer_error: '答案错误',
            question_error: '题目错误',
            option_error: '选项错误',
            explanation_error: '解析错误',
            other: '其他问题',
          };
          const errorTypeName = errorTypeMap[errorType] || errorType;
          const userName = currentUser?.username || currentUser?.phone || '匿名用户';

          for (const recipient of recipients) {
            await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                action: 'messages_send_notification',
                userId: recipient.id,
                title: '用户纠错反馈',
                content: `用户「${userName}」对题目「${questionTitle || questionId}」提交了纠错反馈。\n\n纠错类型：${errorTypeName}\n详细描述：${description.trim()}`,
                category: 'system',
                priority: 'normal',
              }),
            });
          }
        }
      } catch (notifyErr) {
        // 通知失败不影响纠错提交结果
        console.error('站内信通知异常:', notifyErr);
      }

      setSubmitResult({ success: true, message: '纠错提交成功，感谢您的反馈！' });
      setTimeout(() => {
        onClose();
        setSubmitResult(null);
        setErrorType('');
        setDescription('');
      }, 2000);
    } catch (error) {
      console.error('提交纠错失败:', error);
      setSubmitResult({
        success: false,
        message: error instanceof Error ? error.message : '提交失败，请稍后重试',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setSubmitResult(null);
      setErrorType('');
      setDescription('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">题目纠错</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5">
          {/* Question Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">题目编号</p>
            <p className="text-sm font-medium text-gray-900">{questionId}</p>
            <p className="text-xs text-gray-500 mt-2 mb-1">题目内容</p>
            <p className="text-sm text-gray-700 line-clamp-2">{questionTitle}</p>
          </div>

          {/* Error Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              纠错类型 <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {ERROR_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setErrorType(type.value)}
                  disabled={isSubmitting}
                  className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                    errorType === type.value
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                  } disabled:opacity-50`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              纠错说明 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
              placeholder="请详细描述您发现的问题..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:opacity-50"
            />
          </div>

          {/* Submit Result */}
          {submitResult && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg ${
                submitResult.success
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}
            >
              {submitResult.success ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span className="text-sm">{submitResult.message}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !errorType || !description.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                提交中...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                提交纠错
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
